import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import * as hrmsService from "../../services/hrmsService";

// Super Admin only: switches the HR add-ons (PAYROLL / EXPENSES / SCHEDULE_BRIDGE) on or off for this one vendor.
// Core HR itself is not a switch here - it follows the vendor's plan (plan_type = 3) - so this panel is read-only
// about that and only ever writes the add-on rows.
const FEATURES = [
  { key: "PAYROLL", label: "Payroll", hint: "Salary structures, payroll runs, payslips." },
  { key: "EXPENSES", label: "Expenses", hint: "Employee expense claims and salary advances/loans. Not built yet - switching this on has no effect in the portal." },
  { key: "SCHEDULE_BRIDGE", label: "Schedule bridge", hint: "HR leave/holidays/roster block booking availability, and lets HR import the vendor's existing booking shifts once." },
];

export default function HrEnablementPanel({ vendorId }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await hrmsService.getEnablement(vendorId);
      if (res && res.success === false) throw new Error(res.message || "Something went wrong");
      setState({ loading: false, error: null, data: res.data });
    } catch (err) {
      setState({ loading: false, error: err.message || "Something went wrong", data: null });
    }
  }, [vendorId]);

  useEffect(() => {
    if (vendorId) load();
  }, [vendorId, load]);

  const toggle = async (featureKey, next) => {
    setSavingKey(featureKey);
    try {
      const res = await hrmsService.setFeatureEnabled(vendorId, featureKey, next);
      if (res?.success === false) {
        toast.error(res.message || "Something went wrong");
      } else {
        setState((s) => ({ ...s, data: { ...s.data, features: { ...s.data.features, [featureKey]: next } } }));
        toast.success(`${featureKey === "SCHEDULE_BRIDGE" ? "Schedule bridge" : featureKey === "PAYROLL" ? "Payroll" : "Expenses"} ${next ? "switched on" : "switched off"}`);
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setSavingKey(null);
    }
  };

  if (!vendorId) return null;

  return (
    <div className="bg-white rounded p-3 mb-3" style={{ border: "1px solid #E9ECEF" }}>
      <h6 className="mb-1">HR module add-ons</h6>
      <div className="text-muted mb-3" style={{ fontSize: 13 }}>
        Core HR itself follows this vendor's plan and cannot be switched here. These add-ons only matter once Core HR is on for them.
      </div>

      {state.loading ? (
        <Spinner animation="border" size="sm" />
      ) : state.error ? (
        <div className="text-danger d-flex align-items-center gap-2" style={{ fontSize: 14 }} role="alert">
          {state.error}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={load}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {!state.data.core_enabled && (
            <div className="mb-3" style={{ fontSize: 13, color: "#92400E", background: "#FEF3C7", borderRadius: 6, padding: "8px 12px" }} role="status">
              Core HR is not enabled for this vendor (their plan doesn't include it). These switches will have no effect until it is.
            </div>
          )}
          {FEATURES.map((f) => (
            <div key={f.key} className="d-flex align-items-start justify-content-between py-2" style={{ borderBottom: "1px solid #F3F4F6" }}>
              <div className="pe-3">
                <div style={{ fontWeight: 600, fontSize: 14 }}>{f.label}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {f.hint}
                </div>
              </div>
              <div className="form-check form-switch" style={{ minWidth: 44 }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id={`hr-feature-${f.key}`}
                  aria-label={f.label}
                  checked={!!state.data.features[f.key]}
                  disabled={savingKey === f.key}
                  onChange={(e) => toggle(f.key, e.target.checked)}
                  style={{ cursor: savingKey === f.key ? "wait" : "pointer" }}
                />
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
